<template>
  <div>
    <v-progress-linear
      fixed
      v-if="loading"
      color="white"
      indeterminate
      class="elevation-25"
      style="z-index: 6"
    />

    <v-app-bar app clipped-left color="primary" class="bright--text">
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            :aria-label="$t('appbar.toggleNav')"
            v-bind="attrs"
            v-on="on"
            @click="$emit('toggle-drawer')"
          >
            <v-icon class="bright--text">mdi-menu</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('appbar.toggleNav') }}</span>
      </v-tooltip>
      <div class="d-flex align-center mr-15">
        <div style="justify-content: center">
          <img
            src="/metaq-bi.png"
            alt="MetaPlanet Logo"
            style="
              max-width: 165px;
              min-width: 165px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
            "
            @click="openMetaplanet"
          />
        </div>
      </div>

      <ContractSelector />

      <v-spacer></v-spacer>

      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            class="bright--text search-btn mr-2"
            depressed
            small
            v-bind="attrs"
            v-on="on"
            @click="$emit('open-search')"
          >
            <v-icon small class="mr-1">mdi-magnify</v-icon>
            <span class="hidden-sm-only">{{ $t('appbar.search') }}</span>
            <kbd class="search-kbd hidden-sm-only">Ctrl+K</kbd>
          </v-btn>
        </template>
        <span>{{ $t('appbar.search') }} (Ctrl+K)</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            class="bright--text"
            icon
            depressed
            :aria-label="$t('appbar.shortcuts')"
            v-bind="attrs"
            v-on="on"
            @click="$emit('open-shortcuts')"
          >
            <v-icon small>mdi-keyboard</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('appbar.shortcuts') }}</span>
      </v-tooltip>

      <notification-bell />

      <div class="clock hidden-sm-only">
        <div class="utc">{{ currentTime }} UTC</div>
        <div class="local">{{ localTime }}</div>
      </div>
      <div class="address hidden-sm-only">{{ address }}</div>
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            class="bright--text"
            v-bind="attrs"
            v-on="on"
            @click="toggleTheme"
            icon
            depressed
            :aria-label="$t('appbar.toggleTheme')"
          >
            <v-icon>
              {{
                !$vuetify.theme.dark
                  ? 'mdi-weather-sunny'
                  : 'mdi-moon-waxing-crescent'
              }}
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('appbar.toggleTheme') }}</span>
      </v-tooltip>
      <v-menu offset-y>
        <template #activator="{ on, attrs }">
          <v-tooltip bottom>
            <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <v-btn
                class="bright--text"
                icon
                depressed
                :aria-label="$t('appbar.language')"
                v-bind="{ ...attrs, ...tooltipAttrs }"
                v-on="{ ...on, ...tooltipOn }"
              >
                <v-icon>mdi-translate</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('appbar.language') }}</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item
            @click="changeLocale('en')"
            :class="{ 'v-list-item--active': $i18n.locale === 'en' }"
          >
            <v-list-item-title>English</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="changeLocale('ko')"
            :class="{ 'v-list-item--active': $i18n.locale === 'ko' }"
          >
            <v-list-item-title>한국어</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <slot name="walletConnect"></slot>
      <slot name="loginButton"></slot>
    </v-app-bar>
  </div>
</template>

<script>
import ContractSelector from '@/components/Contract/ContractSelector.vue'
import NotificationBell from '@/components/Application/NotificationBell.vue'

export default {
  name: 'AppBar',
  components: { ContractSelector, NotificationBell },

  data() {
    return {
      currentTime: new Date().toISOString().replace('T', ' ').slice(0, -5),
      localTime: '',
    }
  },
  computed: {
    loading() {
      return this.$store.getters.loading
    },
    address() {
      return this.$store.getters['auth/address']
    },
  },
  methods: {
    openMetaplanet() {
      window.open('https://www.metaplanet.tech', '_blank')
    },
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark
      localStorage.setItem(
        'vuetifyTheme',
        JSON.stringify({ dark: this.$vuetify.theme.dark })
      )
    },
    changeLocale(locale) {
      this.$i18n.locale = locale
      localStorage.setItem('tms_locale', locale)
    },
  },
  mounted() {
    const localeMapInit = { ko: 'ko-KR', en: 'en-US' }
    const localeInit =
      localeMapInit[this.$i18n.locale] || this.$i18n.locale || undefined
    this.localTime = new Date().toLocaleString(localeInit)
    this._clockInterval = setInterval(() => {
      this.currentTime = new Date().toISOString().replace('T', ' ').slice(0, -5) // 1초마다 현재 시간 업데이트
      const localeMap = { ko: 'ko-KR', en: 'en-US' }
      const locale =
        localeMap[this.$i18n.locale] || this.$i18n.locale || undefined
      this.localTime = new Date().toLocaleString(locale)
    }, 1000)
  },
  beforeDestroy() {
    if (this._clockInterval) {
      clearInterval(this._clockInterval)
      this._clockInterval = null
    }
  },
}
</script>
<style scoped>
.address {
  user-select: none;
  font-size: 13px;
  width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  margin-right: 8px;
  transition: all 0.2s ease;
}
.address:hover {
  background: rgba(255, 255, 255, 0.15);
}
.clock {
  width: 220px;
  margin: 0 16px;
  user-select: none;
  text-align: right;
  white-space: nowrap;
}
.clock .utc {
  font-weight: 500;
  font-size: 14px;
  color: #b0bec5;
}
.clock .local {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}
.search-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 6px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  font-size: 13px;
}
.search-btn:hover {
  background: rgba(255, 255, 255, 0.18) !important;
}
.search-kbd {
  font-size: 10px;
  font-family: inherit;
  margin-left: 8px;
  padding: 1px 5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 16px;
}
.contract {
  width: 400px;
  margin: 0 16px;
  user-select: none;
  text-align: right;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}
</style>
